import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import ForgotPasswordModal from './ForgotPasswordModal';

interface LoginFormProps {
  onClose: () => void;
  onSwitchToRegister: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose, onSwitchToRegister }) => {
  const { login, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear general error
    if (generalError) {
      setGeneralError('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!validateForm()) {
      return;
    }
    
    if (isSubmitting) {
      console.log('🔒 Already submitting, ignoring duplicate submit'); // Debug log
      return;
    }
    
    try {
      setIsSubmitting(true);
      console.log('🔐 Attempting login...'); // Debug log
      
      const result = await login(formData.username, formData.password);
      console.log('🔐 Login result:', result); // Debug log
      
      if (result.success) {
        console.log('✅ Login successful, closing form'); // Debug log
        onClose();
        return;
      }
      
      // Login failed - handle error and keep form open
      console.log('❌ Login failed:', result.message); // Debug log
      
      // Handle authentication errors without page reload or alerts
      if (result.message.includes('Invalid username or password') || result.message.includes('Invalid')) {
        setGeneralError('Invalid credentials');
      } else {
        setGeneralError(result.message);
      }
      
      // Clear password field for security
      setFormData(prev => ({
        ...prev,
        password: ''
      }));
      
      // Explicitly prevent form closure on error
      console.log('🔒 Keeping form open due to login error'); // Debug log
      
    } catch (error: any) {
      console.error('Login form error:', error);
      
      // Handle network/server errors without alerts
      if (error.response?.status === 401) {
        setGeneralError('Invalid credentials');
      } else if (error.response?.status === 429) {
        setGeneralError('Too many login attempts. Please wait a few minutes and try again.');
      } else if (error.response?.data?.message) {
        setGeneralError(error.response.data.message);
      } else {
        setGeneralError('Login failed. Please check your connection and try again.');
      }
      
      // Clear password field for security
      setFormData(prev => ({
        ...prev,
        password: ''
      }));
      
      // Explicit return to prevent any further execution
      
    } finally {
      setIsSubmitting(false);
      console.log('🔓 Login attempt completed, form should remain open'); // Debug log
    }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome Back
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
        >
          ×
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {generalError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
          >
            <p className="text-red-600 dark:text-red-400 text-sm">{generalError}</p>
          </motion.div>
        )}
        
        <Input
          name="username"
          type="text"
          label="Username or Email"
          placeholder="Enter your username or email"
          value={formData.username}
          onChange={handleInputChange}
          error={errors.username}
          disabled={isLoading}
        />
        
        <Input
          name="password"
          type="password"
          label="Password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleInputChange}
          error={errors.password}
          disabled={isLoading}
        />
        
        <Button
          type="submit"
          className="w-full"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Signing In...' : 'Sign In'}
        </Button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Don't have an account?{' '}
          <button
            onClick={onSwitchToRegister}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Sign up
          </button>
        </p>
      </div>
      
      <div className="mt-4 text-center">
        <button
          onClick={() => setShowForgotPassword(true)}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
        >
          Forgot your password?
        </button>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </div>
  );
};

export default LoginForm;
